/**
 * A caixa do mês do banco de horas, para adicionar e para editar.
 *
 * Papel: reproduz `#modal-overlay-bh-entry` da origem — o título "Adicionar
 * mês" ou "Editar mês", o nome da pessoa logo abaixo, PERÍODO em linha
 * inteira, CRÉDITO e DÉBITO lado a lado, AJUSTE opcional em linha inteira, e
 * "Cancelar" e "Salvar" — e grava por `useSalvarLancamentoMensal`.
 *
 * Como usar: quem monta põe o componente na tela só enquanto a caixa está
 * aberta, com uma `key` diferente por mês, e o tira dela em `onSalvo` e em
 * `onCancelar`. Sem a `key`, o React reaproveitaria os campos de um mês no
 * seguinte.
 *
 * Na edição o período fica travado, como em `bhOpenEditEntry`: é o que mantém o
 * par pessoa + período único. Travado, ele não esmaece (`disabled:opacity-100`),
 * porque a origem não dá ao campo desabilitado desenho próprio. A observação
 * do mês não tem campo na caixa, e vai no corpo da edição como estava gravada —
 * sem isso, corrigir o crédito apagaria a explicação de um ajuste.
 *
 * As recusas saem em `alert()`, como na origem: o período fora de `AAAA-MM`, o
 * período que a pessoa já tem (conferido nos meses carregados, antes da API), a
 * primeira mensagem do schema e a recusa da API. Com a recusa, a caixa fica
 * aberta com o que estava digitado.
 */
import {
  type ColaboradorBancoHoras,
  type LancamentoMensal,
  novoLancamentoMensalSchema,
} from '@concierge/shared';
import { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';

import { ErroDaApi } from '@/shared/api';
import { cn } from '@/shared/lib/utils';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { formatarHoras } from '../banco-horas';
import { useSalvarLancamentoMensal } from '../hooks/usar-banco-horas';
import {
  CLASSES_DA_GRADE,
  CLASSES_DO_CAMPO,
  CLASSES_DO_GRUPO,
  CLASSES_DO_GRUPO_INTEIRO,
  CLASSES_DO_ROTULO,
  handleAbrir,
  handleImpedirFechamento,
} from './caixa-da-origem';

interface FormularioMesProps {
  /** A pessoa do mês, com os meses que ela já tem. */
  colaborador: ColaboradorBancoHoras;
  /** O mês a editar, ou `null` para adicionar. */
  mes: LancamentoMensal | null;
  /** Chamado depois de gravar e de o banco de horas ser lido de novo. */
  onSalvo: () => void;
  /** Chamado ao clicar em "Cancelar". */
  onCancelar: () => void;
}

/** Os campos como o formulário os guarda: texto, como os campos nativos devolvem. */
interface CamposDoMes {
  periodo: string;
  credito: string;
  debito: string;
  ajuste: string;
}

/** O que o `alert()` diz numa falha ao gravar que não veio da API. */
const FALHA_AO_SALVAR = 'Não foi possível salvar o registro.';

/**
 * Os valores com que os campos abrem.
 *
 * @param mes - O mês a editar, ou `null`.
 * @returns Vazios ao adicionar; na edição, o período e os tempos em `HH:MM`,
 *   com o ajuste zero vazio, como `bhOpenEditEntry` faz.
 */
function valoresIniciais(mes: LancamentoMensal | null): CamposDoMes {
  if (mes === null) return { periodo: '', credito: '', debito: '', ajuste: '' };
  return {
    periodo: mes.competencia,
    credito: formatarHoras(mes.creditoMin),
    debito: formatarHoras(mes.debitoMin),
    ajuste: mes.ajusteMin === 0 ? '' : formatarHoras(mes.ajusteMin),
  };
}

/**
 * A caixa do mês.
 *
 * @param colaborador - A pessoa, com os meses, para o nome e a conferência do período.
 * @param mes - O mês a editar, ou `null` para adicionar.
 * @param onSalvo - Chamado depois da gravação aceita.
 * @param onCancelar - Chamado ao clicar em "Cancelar".
 * @returns O `Dialog` aberto, com os campos e os dois botões.
 */
export function FormularioMes({ colaborador, mes, onSalvo, onCancelar }: FormularioMesProps) {
  const { register, getValues } = useForm<CamposDoMes>({
    defaultValues: valoresIniciais(mes),
  });
  const salvar = useSalvarLancamentoMensal();
  // "Cancelar" segue ativo durante a gravação; se ela volta depois da caixa
  // desmontada, `onSalvo` fecharia a caixa que a pessoa abriu depois.
  const montada = useRef(true);

  useEffect(() => {
    montada.current = true;
    return () => {
      montada.current = false;
    };
  }, []);

  /** Confere, monta e grava o mês, na ordem de `bhSaveEntry`. */
  async function handleSalvar() {
    const valores = getValues();
    const periodo = (mes?.competencia ?? valores.periodo).trim();

    if (!/^\d{4}-\d{2}$/.test(periodo)) {
      window.alert('Informe o período no formato AAAA-MM.');
      return;
    }
    if (mes === null && colaborador.meses.some((atual) => atual.competencia === periodo)) {
      window.alert('Esse período já existe para este colaborador.');
      return;
    }

    const corpo = novoLancamentoMensalSchema.safeParse({
      colaboradorId: colaborador.id,
      competencia: periodo,
      creditoMin: valores.credito,
      debitoMin: valores.debito,
      ajusteMin: valores.ajuste,
      observacao: mes?.observacao ?? null,
    });
    if (!corpo.success) {
      window.alert(corpo.error.issues[0]?.message ?? FALHA_AO_SALVAR);
      return;
    }

    try {
      await salvar.mutateAsync({ id: mes?.id ?? null, dados: corpo.data });
      if (montada.current) onSalvo();
    } catch (erro) {
      window.alert(erro instanceof ErroDaApi ? erro.message : FALHA_AO_SALVAR);
    }
  }

  return (
    <Dialog
      open
      onOpenChange={(aberta) => {
        if (!aberta) onCancelar();
      }}
    >
      <DialogContent
        aria-describedby={undefined}
        onOpenAutoFocus={handleAbrir}
        onEscapeKeyDown={handleImpedirFechamento}
        onPointerDownOutside={handleImpedirFechamento}
        onInteractOutside={handleImpedirFechamento}
      >
        <DialogHeader>
          <DialogTitle>{mes === null ? 'Adicionar mês' : 'Editar mês'}</DialogTitle>
        </DialogHeader>

        <div className="mb-4 border-b border-linha pb-2.5 text-xs/[1.6] font-semibold text-texto-medio">
          {colaborador.nomeCompleto}
        </div>

        <div className={CLASSES_DA_GRADE}>
          <div className={CLASSES_DO_GRUPO_INTEIRO}>
            <Label htmlFor="bh-periodo" className={CLASSES_DO_ROTULO}>
              PERÍODO (AAAA-MM)
            </Label>
            <Input
              id="bh-periodo"
              type="text"
              className={cn(CLASSES_DO_CAMPO, 'disabled:opacity-100')}
              placeholder="2026-07"
              maxLength={7}
              disabled={mes !== null}
              {...register('periodo')}
            />
          </div>

          <div className={CLASSES_DO_GRUPO}>
            <Label htmlFor="bh-credito" className={CLASSES_DO_ROTULO}>
              CRÉDITO (HH:MM)
            </Label>
            <Input
              id="bh-credito"
              type="text"
              className={CLASSES_DO_CAMPO}
              placeholder="00:00"
              {...register('credito')}
            />
          </div>

          <div className={CLASSES_DO_GRUPO}>
            <Label htmlFor="bh-debito" className={CLASSES_DO_ROTULO}>
              DÉBITO (HH:MM)
            </Label>
            <Input
              id="bh-debito"
              type="text"
              className={CLASSES_DO_CAMPO}
              placeholder="00:00"
              {...register('debito')}
            />
          </div>

          <div className={CLASSES_DO_GRUPO_INTEIRO}>
            <Label htmlFor="bh-ajuste" className={CLASSES_DO_ROTULO}>
              AJUSTE (HH:MM) — opcional
            </Label>
            <Input
              id="bh-ajuste"
              type="text"
              className={CLASSES_DO_CAMPO}
              placeholder="00:00"
              {...register('ajuste')}
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            className="text-sm/[normal]"
            onClick={onCancelar}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            className="text-sm/[normal]"
            onClick={() => void handleSalvar()}
            disabled={salvar.isPending}
          >
            Salvar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
